import { useEffect, useRef, useState } from "react";
import { saveAs } from "file-saver";
import { Link, Outlet, useNavigate, useRouteLoaderData } from "react-router-dom";
import UserInfo from "src/components/common/userInfo";
import { getFileNameFromUrl, mimeTypeMap, myAxios, scroll } from "src/components/common/functions";
import { ReactComponent as Download } from "src/img/svg/download.svg";
import { ReactComponent as Upload } from "src/img/svg/upload.svg";
import { Billing, defaultBilling } from "./types";

import { useUiStore } from "src/components/main/uiStore";
export default function BillingDetail() {
    const routeBilling = (useRouteLoaderData("Billing") as Billing | undefined) ?? defaultBilling;
    const setShowLoader = useUiStore(state => state.setShowLoader);
    const navigate = useNavigate();
    const receiptUploadRef = useRef<HTMLInputElement | null>(null);
    const [billing, setBilling] = useState<Billing>(routeBilling);
    const [curPdfUrl, setCurPdfUrl] = useState<string>(routeBilling.pdfId ?? "");

    useEffect(() => {
        setBilling(routeBilling);
        setCurPdfUrl(routeBilling.pdfId ?? "");
    }, [routeBilling]);

    const billingId = String(billing.id ?? "");
    const isVoided = billing.void === "yes";
    const isBilled = billing.billed === "yes";
    const pdfUrl = curPdfUrl;
    const durationDays =
        billing.fromDate && billing.toDate
            ? Math.floor((new Date(`${billing.toDate}T00:00:00`).valueOf() - new Date(`${billing.fromDate}T00:00:00`).valueOf()) / 86400000) + 1
            : null;

    const refreshBilling = async () => {
        if (!billingId) return;
        const formData = new FormData();
        formData.append("id", billingId);
        const res = await myAxios({ fileName: "billings/readBilling.php", formData });
        if (!res || !res.data) return;
        setBilling(res.data as Billing);
        setCurPdfUrl((res.data as Billing).pdfId ?? "");
    };

    const runMutation = async (fileName: string, extra: Record<string, string | File>) => {
        const formData = new FormData();
        formData.append("id", billingId);
        formData.append("billingNumber", billing.billingNumber ?? "");
        formData.append("start", billing.fromDate ?? "");
        formData.append("end", billing.toDate ?? "");
        Object.entries(extra).forEach(([key, value]) => formData.append(key, value));
        setShowLoader(true);
        const res = await myAxios({ fileName, formData });
        setShowLoader(false);
        if (!res) return false;
        await refreshBilling();
        return true;
    };

    const handlePdfUpload = async (target: HTMLInputElement | DataTransfer) => {
        const file = target.files?.[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".pdf")) {
            window.alert("Please select PDF only.");
            if ("value" in target) target.value = "";
            return;
        }
        if (pdfUrl && !window.confirm("Are you sure to overwrite the existing billing file?")) {
            if ("value" in target) target.value = "";
            return;
        }
        const updated = await runMutation("billings/addPDF.php", { pdf: file });
        if (updated) {
            navigate(`/Billings/${billingId}`, { replace: true });
            scroll(null);
        }
        if ("value" in target) target.value = "";
    };

    const handleDownloadPdf = async () => {
        if (!pdfUrl) return;
        try {
            const response = await fetch(pdfUrl);
            if (!response.ok) throw new Error("Failed to fetch file");
            saveAs(await response.blob(), getFileNameFromUrl(pdfUrl) || `${billing.billingNumber || "billing"}.pdf`);
        } catch (error) {
            window.alert("Error downloading file: " + error);
        }
    };
    const handleRemovePdf = async () => {
        if (!pdfUrl) return;
        if (!window.confirm("Are you sure to remove this billing file?")) return;
        const updated = await runMutation("billings/deleteBillingPDF.php", {});
        if (!updated) return;
        setCurPdfUrl("");
    };

    const rows = [
        {
            label: "Status",
            value: <span className="badge text-bg-primary">{billing.status || "draft"}</span>,
        },
        {
            label: "Billing Number",
            value: billing.billingNumber || "",
        },
        {
            label: "Project",
            value: billing.projectId ? <Link to={`/Projects/${billing.projectId}`}>{billing.projectId}</Link> : "",
        },
        {
            label: "Contact",
            value: billing.contactName || "",
            show: !!billing.contactId || !!billing.contactName,
        },
        {
            label: "Requester",
            value: billing.requester?.value || billing.requester?.label ? (
                <UserInfo name={billing.requester?.label ?? ""} id={billing.requester?.value} />
            ) : (
                ""
            ),
            show: !!billing.requester?.value || !!billing.requester?.label,
        },
        {
            label: "Requester Department",
            value: billing.requester?.department || "",
            show: !!billing.requester?.department,
        },
        {
            label: "Requester Outside",
            value: billing.requester?.outside || "",
            show: !!billing.requester?.outside,
        },
        {
            label: "Requester Projects",
            value: billing.requester?.projects || "",
            show: !!billing.requester?.projects,
        },
        {
            label: "Billing Dates",
            value: [billing.fromDate, billing.toDate].filter(Boolean).join(" - "),
        },
        {
            label: "Duration",
            value: durationDays === 1 ? "1 day" : durationDays ? `${durationDays} days` : "",
            show: durationDays !== null,
        },
        {
            label: "Amount",
            value: billing.amount ? `$${billing.amount}` : "",
        },
        {
            label: "Billable",
            value: billing.billable || "",
        },
        {
            label: "Billed",
            value: billing.billed || "no",
        },
        {
            label: "Notes",
            value: <span style={{ whiteSpace: "pre-wrap" }}>{billing.notes || ""}</span>,
        },
        {
            label: "Submitter",
            value: billing.submitterId || billing.submitterName ? (
                <>
                    <UserInfo name={billing.submitterName ?? ""} id={billing.submitterId} />
                    <span className="ms-2">{billing.submitTime || ""}</span>
                </>
            ) : (
                billing.submitTime || ""
            ),
            show: !!billing.submitterId || !!billing.submitterName || !!billing.submitTime,
        },
        {
            label: "Approver",
            value: billing.approver?.value || billing.approver?.label ? (
                <UserInfo name={billing.approver?.label ?? ""} id={billing.approver?.value} />
            ) : (
                ""
            ),
            show: !!billing.approver?.value || !!billing.approver?.label,
        },
        {
            label: "Approval Time",
            value: billing.approvalTime || "",
            show: !!billing.approvalTime,
        },
        {
            label: "Approver Notes",
            value: <span style={{ whiteSpace: "pre-wrap" }}>{billing.approverNotes || ""}</span>,
            show: !!billing.approverNotes,
        },
        {
            label: "Notified By",
            value: billing.notifiedBy || billing.notifierName ? (
                <>
                    <UserInfo name={billing.notifierName ?? ""} id={billing.notifiedBy} />
                    <span className="ms-2">{billing.notifiedAt || ""}</span>
                </>
            ) : (
                billing.notifiedAt || ""
            ),
            show: !!billing.notifiedAt || !!billing.notifiedBy || !!billing.notifierName,
        },
        {
            label: "Creator",
            value: billing.creatorId || billing.creatorName ? (
                <>
                    <UserInfo name={billing.creatorName ?? ""} id={billing.creatorId} />
                    <span className="ms-2">{billing.createdAt || ""}</span>
                </>
            ) : (
                billing.createdAt || ""
            ),
            show: !!billing.creatorId || !!billing.creatorName || !!billing.createdAt,
        },
        {
            label: "Updater",
            value: billing.updaterId || billing.updaterName ? (
                <>
                    <UserInfo name={billing.updaterName ?? ""} id={billing.updaterId} />
                    <span className="ms-2">{billing.updatedAt || ""}</span>
                </>
            ) : (
                billing.updatedAt || ""
            ),
            show: !!billing.updaterId || !!billing.updaterName || !!billing.updatedAt,
        },
        {
            label: "Void Reason",
            value: billing.voidReason || "",
            show: isVoided,
        },
    ];

    return (
        <div className="cssContent pb8rem pt-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                <div>
                    <h3 className="mb-1">{billing.billingNumber || "Billing Detail"}</h3>
                    <div className="text-muted">Billing ID: {billingId || "-"}</div>
                </div>
            </div>
            <input
                type="file"
                accept={mimeTypeMap.pdf}
                ref={receiptUploadRef}
                style={{ display: "none" }}
                onChange={event => void handlePdfUpload(event.currentTarget)}
            />
            <div className="table-responsive" style={{ minWidth: "800px", maxWidth: "90%", margin: "20px auto" }}>
                <table className="table">
                    <tbody>
                        {rows
                            .filter(row => row.show !== false)
                            .map(row => (
                                <tr key={row.label}>
                                    <th style={{ width: "220px" }}>{row.label}</th>
                                    <td>{row.value || <span className="text-muted">-</span>}</td>
                                </tr>
                            ))}
                        {billing.data ? (
                            <tr>
                                <th>Data</th>
                                <td>
                                    <pre className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                                        {JSON.stringify(billing.data, null, 2)}
                                    </pre>
                                </td>
                            </tr>
                        ) : null}
                        <tr>
                            <th>File</th>
                            <td
                                onDragOver={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                                onDrop={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    void handlePdfUpload(event.dataTransfer);
                                }}
                            >
                                <button
                                    type="button"
                                    className="mb-0 btn btn-primary"
                                    onClick={() => receiptUploadRef.current?.click()}
                                >
                                    <Upload />
                                </button>
                                {pdfUrl ? (
                                    <>
                                        <button
                                            type="button"
                                            className="btn btn-success ms-3"
                                            onClick={handleDownloadPdf}
                                        >
                                            <Download />
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-danger ms-3 text-white"
                                            onClick={() => void handleRemovePdf()}
                                        >
                                            &times;
                                        </button>
                                    </>
                                ) : null}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
                <div className="d-flex flex-wrap gap-2">
                    {!isVoided ? (
                        <>
                            <button
                                type="button"
                                className="btn btn-success"
                                onClick={async () => {
                                    if (!window.confirm("Are you sure to notify the approver?")) return;
                                    await runMutation("billings/notify.php", {});
                                }}
                            >
                                Notify Approver
                            </button>
                            <button type="button" className="btn btn-primary" onClick={() => navigate("Edit")}>
                                Edit Bill
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={async () => {
                                    if (!window.confirm("Are you sure to void this Bill?")) return;
                                    let voidReason = window.prompt("reason to void:");
                                    if (voidReason === null) return;
                                    while (voidReason === "") {
                                        window.alert("Reason is required to void.");
                                        voidReason = window.prompt("reason to void:");
                                        if (voidReason === null) return;
                                    }
                                    const updated = await runMutation("billings/deleteBilling.php", { voidReason });
                                    if (updated) navigate(`/Billings/${billingId}`, { replace: true });
                                }}
                            >
                                Void Bill
                            </button>
                        </>
                    ) : (
                        <button
                            type="button"
                            className="btn btn-danger"
                            onClick={async () => {
                                if (!window.confirm("Are you sure to re-valid this billing?")) return;
                                await runMutation("billings/restoreBilling.php", {});
                            }}
                        >
                            Valid
                        </button>
                    )}
                    {!isVoided && billing.status?.toLowerCase() === "approved" ? (
                        <button
                            type="button"
                            className={`btn btn-${isBilled ? "danger" : "success"}`}
                            onClick={async () => {
                                if (!window.confirm(`Are you sure to set this as ${isBilled ? "unbilled" : "billed"}?`)) return;
                                await runMutation("billings/billed.php", { billed: isBilled ? "" : "yes" });
                            }}
                        >
                            {isBilled ? "Set Unbilled" : "Set Billed"}
                        </button>
                    ) : null}
                    <button type="button" className="btn btn-outline-primary ms-auto" onClick={() => navigate("/Billings")}>
                        Back
                    </button>
                </div>
            <Outlet />
        </div>
    );
}
